/**
 * Frontend domain contracts (PRD-aligned).
 *
 * These types are intended to be stable and shared between UI and API adapters.
 * They intentionally do NOT mirror the current backend implementation.
 *
 * T-12: All field names use camelCase. The response transformer in HttpBennieClient
 * converts snake_case API responses to camelCase automatically.
 */

export type ReportType =
  | 'validation'
  | 'audit'
  | 'forecast'
  | 'custom';

export type WorkspaceStatus = 'active' | 'draft' | 'archived';

// ServiceSelection uses snake_case intentionally — fields are bidirectional
// (passed through to API in request bodies, read from API responses).
// The response transformer converts snake_case→camelCase, but these fields
// need to match what the API expects in both directions.
export interface ServiceSelection {
  vertical?: string;
  modelPath?: string[];
  serviceType?: string;
  // Snake_case aliases for backward compat (pre-transformKeys callers)
  model_path?: string[];
  service_type?: string;
  [key: string]: unknown;
}

export interface Workspace {
  id: string;
  name: string;
  description?: string;
  reportType: ReportType;
  status: WorkspaceStatus;
  createdAt: string;
  updatedAt: string;
  serviceSelection?: ServiceSelection;  // Stored in backend, not localStorage
  gdAvailable?: boolean;
  // Workspace report-output language. Per ADR-041 canonical values: 'en' | 'fr' | 'zh-CN'.
  // Set at workspace creation, immutable thereafter. Drives backend LLM directive injection
  // (see LanguageDirective.cs) and per-language prompt-set selection (ADR-042 Path C).
  outputLanguage?: 'en' | 'fr' | 'zh-CN';
  // WI-25 — THE REGISTRY MODEL THIS WORKSPACE HANGS OFF. `workspaces.model_id` (0046), stamped once
  // at create and immutable thereafter.
  //
  // ⚠️ THIS IS A DECLARATION, NOT A NEW FIELD ON THE WIRE, AND THE DIFFERENCE COST ME A WRONG
  // FINDING. `Workspace.ModelId` has carried `[JsonPropertyName("model_id")]` since WI-06, the list
  // endpoint returns the enriched records whole, and `transformKeys` is a TOTAL deep transform with
  // no allowlist — so `modelId` has been ARRIVING in this client all along and TypeScript was
  // discarding it. I measured this interface, then asserted a fact about the wire, and filed
  // "criterion 2 is blocked on the wire" on the strength of it.
  //
  // ⚠️ READ VIA THE DOC, NOT THE COLUMN. Every doc-store read is `SELECT doc`, so this value comes
  // from `doc->>'model_id'` and the lifted COLUMN has no application reader at all. The column
  // exists for the FK and the eventual NOT NULL. A row whose column and doc disagreed would be
  // invisible to every application path — see the divergence probe recorded in the plan doc.
  //
  // ⚠️ EXPECT NULL, AND THAT IS THE DECLARED PHASE, NOT A DEFECT. No production writer sets it:
  // `apps/web` sends no model id on create until WI-44. Measured on DEV 2026-08-22: 4 of 7
  // workspaces populated, 3 NULL. A consumer must render the absence, never invent a model.
  modelId?: string;
  // WI-02: current workflow_state from the BE list endpoint enrichment. Empty
  // string when the workspace has no workflow_state doc yet (pre-onboarding).
  // Drives the WorkflowStatePill on the projects list + the workspace-level
  // read-only contract (mutation affordances hidden when state === 'complete').
  workflowState?: string;
  // ➕ G3 (2026-09-22) — THE TERMINAL-STATE INSTANT: when this project reached
  // `section_finalizing`, from `workflow_state.doc->>'section_finalizing_at'` (migration 0040).
  // Populated by BOTH `listWorkspaces` and `getWorkspace`, out of the same reads that already
  // produce `workflowState` — no extra round trip on either.
  //
  // ⛔ IT IS NOT `updatedAt`, AND SUBSTITUTING IT IS THE DEFECT 0040 EXISTS TO FIX. An export
  // writes back to the workspace after completion, so `updatedAt` moves again afterwards; using it
  // to date a report dates it by when someone last downloaded it. The two answer different
  // questions and neither stands in for the other.
  //
  // ⚠️ NULL/ABSENT IS A REAL, PERMANENT ANSWER FOR SOME PROJECTS — not a loading state. Either the
  // project has not reached `section_finalizing`, or it reached it before the stamp shipped
  // (migration 0041 marks those rows so they stay unmeasurable rather than acquiring a fabricated
  // instant on a later state re-entry). ⛔ A consumer must state its denominator rather than treat
  // absent as zero: a chart that silently drops unstamped projects reports a smaller portfolio as
  // a complete one.
  sectionFinalizingAt?: string | null;
  // The persisted CK retrieval-scoping tags — the flat union of the dimension values the
  // user selected in the picker (raw lowercase slugs, e.g. ["mortgage","irb","pd"]).
  // Absent/empty on a generic or untagged workspace. DISPLAY surfaces humanize these via
  // `labelsForAppliedTo`; the raw slug stays canonical everywhere it matters.
  appliedTo?: string[];
  // Cycle-4 W8 WI-27 (2026-05-25): persist-then-render channel for the latest
  // export's pandoc soft-warning records. Read by the FE on workspace-load to
  // drive the ExportSoftWarningsBanner. Per `core-principles.md` Persist-Then-
  // Render: never read from the export response payload directly — that drives
  // the immediate Snackbar UX; the persistent banner reads from the doc store via
  // this field. Null/empty when no export has run or the latest export emitted
  // zero soft warnings.
  // Element keys are camelCase because transformKeys is DEEP — the wire's
  // `section_id` / `message_sanitized` / … are renamed inside each record too. Declaring
  // them snake_case would leave the banner reading undefined on every row and rendering
  // its "no detail" placeholder for warnings that in fact carry full detail.
  latestExportSoftWarnings?: Array<{
    sectionId?: string | null;
    // WI-80 — the section's human title, when the renderer had one. `sectionId` alone is an
    // opaque token: the live warning this was added for pointed at section "6", which tells a
    // reader nothing about where in their report to look. Optional because two producers
    // (echart resolution) fire before any title is in scope; the banner falls back to the id.
    sectionTitle?: string | null;
    kind?: string | null;
    messageSanitized?: string | null;
    messageRawTruncated?: string | null;
    occurredAt?: string | null;
  }> | null;
  // WI-26 — WHAT THE SERVER SAYS THIS CALLER MAY DO, projected onto the read so the data panel
  // stops deciding for itself.
  //
  // ⚠️ PROJECTED BY `GET /api/workspaces/{id}` ONLY. `listWorkspaces` does not compute it, so a
  // workspace handed to the page from the projects list carries NO capability block — which is
  // exactly why WorkspacePage completes the single-workspace read even when it was given an
  // `initialWorkspace`.
  //
  // ⛔ ABSENT IS NOT "NOTHING IS ALLOWED". A consumer that resolves an absent block to `disabled`
  // has manufactured a refusal from a measurement that never ran, and a disabled control and a
  // failed read are indistinguishable to a user while meaning opposite things. Render an error.
  // See `deriveDataPanelGates` in `utils/dataPanelGates.ts`, which is the ONE place this is read.
  capabilities?: WorkspaceCapabilities;
}

/**
 * WI-26 — the projected capability block.
 *
 * One member today, and it names its ACTION rather than sitting flat on the workspace: the server
 * computes a verdict about the source-data upload surface specifically, and a bare top-level
 * `eligible` would read as a verdict about everything.
 */
export interface WorkspaceCapabilities {
  /** Can this workspace accept a source-data upload right now? */
  sourceDataUpload?: WorkspaceCapability;
}

/**
 * One capability verdict, in the shape `GET /api/workspaces/export-targets` already ships.
 *
 * `reasonCode` is one of `WorkspaceImportGate.Reasons` (apps/api) — internal vocabulary that must
 * never reach the DOM; resolve it through `resolveDataPanelIneligibility`.
 */
export interface WorkspaceCapability {
  eligible?: boolean;
  reasonCode?: string | null;
  /**
   * F-BUSY-01 — the i18next `context` slug the server ships BESIDE the code, and without which the
   * code is not a sentence. `reasonCode` is a coarse bucket: `workspace_busy` is the fall-through
   * for every non-error state before the report-generation cutoff, which on this surface is a
   * brand-new project — nothing is running, so its shipped line ("This project is busy — wait for
   * the current step to finish") is false for the entire population that can reach it. The slug
   * (`before_plan`) is what selects the true sentence, and the 409 the upload arms return has always
   * carried it. Null when eligible, and null for a refusal whose state has no phase.
   */
  context?: string | null;
}

export type PlanStatus = 'draft' | 'generating' | 'approved';

export interface PlanItem {
  id: string;
  title: string;
  description?: string;
  requirements?: string[];
  // Render shape (matches backend plan-structure output). Independent of `category`.
  // `table` identifies table-structured sections routed through the content-gen table mode;
  // `verbatim` identifies locked template-reproduced text; `test_run` is the legacy test
  // tool path (Reggie has since superseded). Default: `report`.
  type?: 'report' | 'test_run' | 'table' | 'verbatim';
  // Section purpose. Decoupled from render shape. `executive_summary` identifies the ES
  // root (descendants inherit by dotted-id prefix) and is what the Phase 2C gate + the
  // /phase2c/refire endpoint filter on. `reference` identifies the single deterministic
  // references/bibliography section (caller-declared, routed out of content-gen).
  // `content` (default) for body sections.
  category?: 'executive_summary' | 'reference' | 'content';
  subItems?: PlanItem[]; // DEPRECATED: Flat architecture — kept for backward compatibility during migration
  // CR17 WI-3: WHAT-vs-HOW split. `expected_output` narrates the deliverable shape;
  // `method` narrates the drafting approach. Both are paired narratives, not recipes.
  method?: string;
  expected_output?: string;
}

/**
 * Cycle 5 WI-05/WI-07 — the WHOLE editable surface of a plan item, and nothing else.
 *
 * ⛔ The absences are the contract. `id` seeds `sections.id`, is the template section-map key and
 * is baked into the export as `{{SECTION:<id>}}`; `category`, `type`, `verbatim_content`,
 * `table_structure`, `tool_slugs` and `dependencies` are structure the plan writer owns; `is_parent`
 * and `subItems` are derived. None of them appears here, so a caller cannot compile its way into
 * the server's `plan_edit_structural_change`.
 *
 * Every field is optional and an ABSENT key means UNTOUCHED, never cleared — an absent optional
 * field and a cleared one are different facts. An explicitly blank value is an explicit clear, and
 * the server refuses it (`plan_edit_enrichment_emptied`) on parent items as well as leaves.
 */
export interface PlanItemProseFields {
  title?: string;
  description?: string;
  method?: string;
  /** Wire key is `expected_output`; the client maps it. */
  expectedOutput?: string;
}

/**
 * The three legal values of a persisted `templateStyleStatus` — mirrors the C# `TemplateStyleStatus`
 * static class (`apps/api/src/Bennie.Api/Models/WorkspaceModels.cs`) exactly; keep the two in sync.
 *
 * 🔴 2026-09-25 review round 2 — REPLACES the former `templateStylePreserved: boolean | null` +
 * warning-nullness inference. That pair let `preserved === false` mean two different facts
 * ("preservation failed" vs "was never attempted") distinguishable only by whether `warning`
 * happened to be null — an inference `TemplateStyleBanner` got wrong once already (rendered the
 * generic-failure banner for a PDF template, whose in-place preservation is DOCX-only per R4). The
 * backend now writes one of these three strings directly, through a write API that cannot construct
 * an invalid pairing, so the FE reads the fact instead of inferring it.
 */
export type TemplateStyleStatus = 'preserved' | 'not_applicable' | 'failed';

export interface Plan {
  id: string;
  workspaceId: string;
  status: PlanStatus;
  version: number;
  createdAt: string;
  updatedAt: string;
  items: PlanItem[];
  // Template Style Preservation state (populated at plan-gen complete).
  // null/undefined → no template was uploaded → no banner, generic export.
  // 'preserved'     → cleanup succeeded → export will use template styling. No warning.
  // 'not_applicable'→ PlanEndpoints.cs R4: in-place preservation is a DOCX property, so a non-DOCX
  //                   (e.g. PDF) template was never eligible for it. NOT a failure — no banner,
  //                   generic export.
  // 'failed'        → a DOCX template was eligible and preservation failed → generic export +
  //                   warning banner. `templateStyleWarning` is always non-null here.
  templateStyleStatus?: TemplateStyleStatus | null;
  templateStyleWarning?: string | null;
  // D-001: true when a template was uploaded and its Word-style snapshot was captured
  // (even if 1:1 cleanup later failed). Drives the scenario-B banner variant:
  // "structure couldn't be preserved, but fonts/spacing still apply".
  templateStyleDictPresent?: boolean;
}

// Per-file ETL lifecycle status. This is the canonical backend taxonomy for a
// source file's processing_status (falling back to status) — the same set
// useETLProgress.ts treats as `knownStatuses`. The two surfaces MUST agree:
// the registry dot in WorkspaceCards and the progress hook read the identical
// persisted field and must interpret it identically (P0-1 / TD-015).
//   pending                          — uploaded, not started
//   processing / processing_phase1   — Phase 1 (structured ingestion) in flight
//   ready_basic                      — Phase 1 done, Phase 2 (indexer) queued/running
//   processing_phase2 / enriching    — Phase 2 actively processing this file
//   ready / ready_full / completed   — Phase 2 done, file fully ingested
//   extraction_failed                — hard Phase-1 failure (terminal)
//   indexing_failed                  — hard Phase-2 failure (terminal)
//   failed                           — generic terminal failure
//   deletion_incomplete              — a delete left one or more stores with
//                                      residue; the record is kept as a retry
//                                      handle (P0-6 fail-loud delete aggregate)
// Adding a new member here without mapping it in WorkspaceCards.sourceFileStatus
// is a BUILD error (never-default exhaustiveness switch) — R-50 contract gate.
export type RegistryItemStatus =
  | 'pending'
  | 'processing'
  | 'processing_phase1'
  | 'processing_phase2'
  | 'enriching'
  | 'ready_basic'
  | 'ready'
  | 'ready_full'
  | 'completed'
  | 'failed'
  | 'extraction_failed'
  | 'indexing_failed'
  | 'deletion_incomplete';

// `retired_evidence` (operator ruling 2026-09-25, "Retire, keep V1"): an auditing evidence file the
// user removed after a report cited it. The server keeps the row so that report still reads; the
// data panel lists it as removed and it takes part in nothing else.
export type FileKind = 'audit_scope' | 'task_description' | 'output_template' | 'model_development_report' | 'source_data' | 'test_results' | 'retired_evidence';

// ── W-16 (wave W2f, 2026-09-05) — THE MDR A WORKSPACE INHERITS ────────────────────────────────
//
// 🔴 THREE OUTCOMES, NOT A NULLABLE ROW, AND THAT IS THE LOAD-BEARING PART. This mirrors the API's
// `PlanMdrOutcome` exactly, whose own docblock names collapsing them as "the laundering this whole
// type exists to refuse":
//
//   * `workspace_not_model_rooted` — the workspace names NO parent model. A PERMANENT property of a
//     pre-0054 row: migration 0054 made `workspaces.model_id` required ON CREATE ONLY, forward-only
//     by ruling `R9`, so the legacy set is EXEMPT from backfill and NEVER DRAINS. ⛔ Its UI arm must
//     therefore carry no retry, no spinner and no disabled control — every one of those tells the
//     reader to wait for something that will not happen.
//   * `no_approved_version` — there IS a parent model and its owner has approved nothing. Transient,
//     and the remedy is on the model, not here. Draft / superseded / withdrawn all land here, by
//     ruling `D-B1`: an inherited MDR is the APPROVED ROW ONLY, because plan generation reads the
//     same rule and a generated plan is a persisted artefact.
//   * `resolved` — exactly one approved version, and `version` names it.
//
// ⛔ The client NEVER re-derives which row is current. `PlanMdrResolver` owns that rule server-side;
// a second spelling here is the pattern `model_mdr_versions` exists to replace.
//   * `pending_approval` — 2026-10-14: the latest version is awaiting approval and feeds plan generation as an
//     unapproved draft reference; `version` names it.
export type InheritedMdrOutcome = 'workspace_not_model_rooted' | 'no_approved_version' | 'resolved' | 'pending_approval';

/**
 * The approved version, projected. Deliberately NARROWER than the ledger row `mdrApi.MdrVersion`
 * carries: no `content_hash`, no `uploaded_by`, no `row_version`. `GET /api/models/{id}/mdr` is
 * AUDITED per read because it discloses those; this workspace-scoped read is not, so it must not
 * become a second, unaudited door onto the same disclosure surface.
 */
export interface InheritedMdrVersion {
  mdrVersionId: string;
  sourceFileName: string;
  status: string;
  createdAt: string;
}

/**
 * ➕ **B15** — the workspace's MOST RECENT persisted export, from
 * `GET /api/workspaces/{id}/exports/latest`.
 *
 * 🔴 **THIS IS WHAT MAKES "Use as model report" A STANDING CONTROL RATHER THAN A TOAST.**
 * B14 `W-12` could only offer the promotion in the seconds after a download, because the
 * export id lived only on the `POST /export` response. Its own comment recorded the cause:
 * *"there is no route that lists a workspace's past exports."* This read is that route's
 * answer, and it is fetched on hydration — so the affordance survives a reload, a dismiss,
 * and a `complete` workspace (the route is a GET, which the workspace gate admits as a read).
 *
 * ⛔ **TWO FIELDS, AND THE NARROWNESS IS THE CONTRACT.** Same discipline as
 * {@link InheritedMdrVersion} directly above: this workspace-scoped read is NOT audited, so
 * it must not become a second door onto what the audited model-ledger read discloses. No
 * content hash, no blob path, no size, no section-version set — the two coordinates
 * `POST /api/models/{modelId}/mdr/from-export` names an export by, and nothing else.
 *
 * `modelId` null/absent is a REAL, PERMANENT state (ruling `R9`, pre-`0054` workspace), and
 * the correct rendering is ABSENCE of the promote control — never a disabled one.
 */
export interface LatestExport {
  exportId: string;
  modelId?: string | null;
}

/** `version` is non-null IFF `outcome` is `resolved` or `pending_approval`; `modelId` is null only when not model-rooted. */
export interface InheritedMdr {
  outcome: InheritedMdrOutcome;
  modelId: string | null;
  version: InheritedMdrVersion | null;
}

/**
 * What the SCREEN has, which is one state more than what the API answers.
 *
 * 🔴 `unreadable` IS NOT A FOURTH OUTCOME — it is the `cannot_measure_fail` house pattern. "The read
 * failed" and "there is nothing to inherit" are different facts, and folding the first into either
 * absence would report a transport failure as an honest answer. `loading` is separate for the same
 * reason: an unresolved read must never render as an absence.
 */
export type InheritedMdrState =
  | { status: 'loading' }
  | { status: 'unreadable' }
  | { status: 'ready'; mdr: InheritedMdr };

export interface WorkspaceFile {
  id: string;
  workspaceId: string;
  kind: FileKind;
  filename: string;
  fileType?: string;
  uploadedAt: string;
  status: RegistryItemStatus;
  // Enrichment fields from data-preprocessor
  contentDescription?: string;
  dataType?: 'unstructured' | 'structured' | 'image';
  keyTopics?: string[];
  hasTables?: boolean;
  tableCount?: number;
  columnNames?: string[];
  rowCount?: number;
  retrievalHint?: 'vector' | 'structured' | 'both';
  sourceSections?: string[];
  localPath?: string;
  blobUrl?: string;
  metadata?: Record<string, unknown>;
  /** Enrichment pipeline status: pending, enriching, done, attention, vision_failed, structured_failed */
  // H-07: Added vision_failed and structured_failed for specific enrichment failure types.
  // TD-317: the BE no longer STAMPS those two — they were bespoke statuses that the metadata-review
  // filter (pending|attention|done) dropped, so the file never reached the dialog and rendered no
  // chip. They moved to `enrichmentErrorStage`, and both paths now stamp `attention`. They stay in
  // this union because the durable catalog still holds them for every file that failed before that
  // deploy; narrowing the union would make those files unrepresentable, not non-existent.
  // WI-14 (file-format overhaul): 'rejected' = a HARD terminal reject — the file could not be read
  // as text at enrichment (blob deleted); its companion `rejectionReason` code says why. Distinct
  // from 'attention' (recoverable review) and from an ETL-phase failure; sourceFileStatus renders it
  // as a terminal notice ahead of every other status.
  enrichmentStatus?: 'pending' | 'enriching' | 'done' | 'enriched' | 'attention' | 'rejected' | 'vision_failed' | 'structured_failed';
  /** WI-14: machine reason code stamped alongside `enrichmentStatus='rejected'` (closed set —
   *  today only 'unsupported_content'; `FileEndpoints.cs::RejectionReasonUnsupportedContent`).
   *  Projected by the knowledge MCP `_CATALOG_FIELDS`. Resolved to prose by `utils/rejectionReason.ts`
   *  (ADR-043: BE ships the code, FE owns the words). Never render raw — it is a code, not a sentence. */
  rejectionReason?: string;
  /** TD-1128: the persisted outcome of the file's layout analysis (`layout_analysis_status` on the
   *  catalog row, written by `LayoutAnalysisRunner.PersistOutcomeAsync`): `succeeded` |
   *  `not_applicable` | `failed` | `stale` | `timed_out`. A failure outcome on an otherwise ready file
   *  renders as "ready with a warning", never as plain ready. */
  layoutAnalysisStatus?: string;
  /** CR16 F-004 / TD-317: backend-recorded stage where enrichment failed — a member of the closed
   *  vocabulary in `FileEndpoints.cs::EnrichmentErrorStages`, resolved to prose by
   *  `utils/enrichmentErrorStage.ts`. Never render this raw: it is a code, not a sentence.
   *  The `enrichmentError` free-text sibling is FULL-DELETED (TD-317/TD-323) — the BE writes no
   *  such field and no consumer may reintroduce a path that paints backend prose. */
  enrichmentErrorStage?: string;
  /** Holistic graph enrichment (librarian) — populated by Phase 3 once P2 ships. */
  cohort?: string | null;
  relationships?: FileRelationshipEdge[];
  /** Librarian graph outcome. `failed` = a TRANSIENT fault a retry may clear; `over_cap` = a
   *  STRUCTURAL over-limit (too many files / input over budget) a retry cannot clear (TD-306).
   *  The FE routes these through `graphChipFor` — the raw value never renders. */
  graphStatus?: 'pending' | 'computed' | 'failed' | 'over_cap';
  graphComputedAt?: string;
  /** EML email metadata (populated when file is an .eml upload) */
  emailSubject?: string;
  emailFrom?: string;
  emailTo?: string;
  emailDate?: string;
  /** For files extracted from EML, the parent EML file ID */
  parentFileId?: string;
  /** Whether this file was extracted as an attachment from an EML */
  isEmailAttachment?: boolean;
}

export type FileRelationshipEdgeType =
  | 'parallel_to'
  | 'bundle_member'
  | 'derived_from'
  | 'derived_from_inverse'
  | 'supersedes'
  | 'superseded_by'
  | 'references'
  | 'referenced_by';

export interface FileRelationshipEdge {
  type: FileRelationshipEdgeType;
  other_file_id: string;
  confidence: 'high' | 'medium' | 'low';
  reasoning?: string;
}

/**
 * Prep-round P-1: severity on CompletenessMissingItem is the canonical int
 * (0=Type1, 1=Type2, 2=Type3, 3=Type4). The FE renderer calls
 * `severityToMuiTone(severity)` from apps/web/src/lib/severity.ts to get the
 * MUI alert tone. The utility also accepts legacy strings (pre-P-1 workspace
 * messages carry 'error'/'warning'/'info' directly from the old BE transform)
 * so the in-memory shape during the transition period is `number | string`.
 *
 * @deprecated kept as a transition-only type; WorkspaceCards still consumes
 * `missingItems` via `as any[]` at the render site. Once legacy completeness
 * messages have cycled out, narrow `CompletenessMissingItem.severity` to
 * `number` and delete this alias.
 */
export type CompletenessSeverity = number | string;

// Phase 2: Structured upload suggestion with actionable details
export interface SuggestedUpload {
  priority: 'high' | 'medium' | 'low';
  whatToUpload: string;
  fileTypes: string[];
  exampleFilenames: string[];
  whereToFind: string;
  whyNeeded: string;
  forSection?: string;
}

export interface CompletenessMissingItem {
  id: string;
  sectionId?: string;
  severity: CompletenessSeverity;
  title: string;
  description?: string;
  recommendedUploads?: string[];
  suggestedUploads?: SuggestedUpload[];  // Phase 2: Structured suggestions
  relatedPlanItemIds?: string[];
}

export interface CompletenessAssessmentFailure {
  code: 'completeness_assessment_failed';
  failed_at: string;
  trigger: string;
  scope: string | null;
  plan_id: string;
  diagnostic_type: string;
}

export interface CompletenessReport {
  workspaceId: string;
  planId: string;
  createdAt: string;
  // BE emits `completeness_check_id`; transformKeys converts to camelCase. Used by
  // the FE polling hook to detect whether a post-mutation rerun has produced a new
  // record (vs. GET returning the pre-mutation stale record because the hook polled
  // before the rerun task was registered on the BE).
  completenessCheckId?: string;
  generationStatus?: 'unknown' | 'current' | 'superseded';
  inputGeneration?: number;
  requestAttribution?: {
    requestId: string; inputGeneration: number; requestedGeneration: number;
    completedGeneration: number | null; claimAttempt: number;
    status: string; planId: string | null; planVersionId: string | null;
  } | null;
  missingItems: CompletenessMissingItem[];
  coverageVerdict?: 'insufficient' | 'partial' | 'adequate' | 'comprehensive';
  // CR5 Phase 4 F-005: overallScore removed from contract — FE gating uses verdict.
  isComplete?: boolean;
  // Phase 1: LLM-generated actionable data
  recommendations?: string[];      // Actionable recommendations from LLM
  criticalGaps?: string[];        // Critical missing items
  // Phase 2: Structured upload suggestions
  suggestedUploads?: SuggestedUpload[];
  // AI per-section coverage verdicts
  sectionCoverage?: Array<{
    sectionId?: string;
    section: string;
    covered: boolean;
    evidence: string;
  }>;
  // Phase 3: Test run detection
  testRunNeeded?: boolean;
  // CR6 Phase 6C F-005 Phase 2: testRunGuidance type physically deleted —
  // test-plan details owned by POST /api/test-runs/plan (CR5 Phase 4).
}

// Backend ALLOWED_SECTION_STATUSES: pending, in_progress, validated, pending_validation, validating, needs_regen, generation_failed, pending_phase_2c, context_unavailable, user_accepted_as_gap
// 'generated' = frontend alias for backend 'completed' (mapped in HttpBennieClient)
// 'approved' = frontend-only state used in usePlanProgress and WorkspaceCards
// 'failed' = mapped from backend 'failed' status in HttpBennieClient
// 'generation_failed' = FB-018 status: insufficient data or exception during generation (§4.2)
// 'pending_phase_2c' = R6.1-F (PF40): executive summary sections deferred to Phase 2C — intentional wait, not an error
// 'context_unavailable' = Batch 18 A-4 / E-10 (PF64): context-engineer hit AGENT_MAX_ROUNDS — section is recoverable via regen or additional source files
// 'exec_summary_blocked' = CR6 Phase 3C-Gate (F-008): ES root status when Phase 2C refuses to fire because one or more body sections are not yet validated. Transient — transitions to validated when blockers recover.
// 'user_accepted_as_gap' = CR20 F-20-04: user explicitly accepted a generation_failed/context_unavailable/needs_regen section as a data gap (no content).
// Canonical section statuses (13). Removed: accepted_pending_holistic, validated_accepted, approved, exec_summary_blocked.
// 'validation_skipped' restored (F-RUN-OOS-03, 2026-07-02): holistic validation threw before adjudicating —
// content preserved, honest non-validated terminal per the-truth.md §3; recover via Regenerate.
export type SectionStatus = 'pending' | 'in_progress' | 'context_ready' | 'generating' | 'pending_validation' | 'validating' | 'validated' | 'validation_skipped' | 'needs_regen' | 'generation_failed' | 'pending_phase_2c' | 'context_unavailable' | 'user_accepted_as_gap';

// CR6 Phase 3C-Gate (F-008): one entry per body section blocking Phase 2C from firing.
// Surfaced on Section.blockingSections when Section.status is 'exec_summary_blocked'.
export interface BlockingSection {
  id: string;
  title: string;
  status: string;
}

export interface Citation {
  id: string;
  source: {
    filename?: string;
    page?: number;
    chunkId?: string;
    // WI-07 (ADR-076 W2): the remaining CitationSource fields the BE has always persisted
    // (SectionModels.cs CitationSource) — modeled so chat citation pop-ups can render the
    // embedded object with full fidelity (image/table preview, curated-source badge) without
    // a fetch. All optional; absent on older persisted citations.
    chunkTitle?: string;
    headingPath?: string;
    contentType?: 'text' | 'image' | 'table';
    blobUrl?: string;
    tableHeaders?: string[];
    tableRows?: string[][];
    fromCuratedIndex?: boolean;
    // Client-CK W3 (WI-18, Q13 lock): curated source class — 'client' (client house view)
    // vs 'bennie' (Kyronyx industry methodology). Absent on home envs and on all legacy
    // persisted citations (the BE omits it when null); drives the preview source-class badge.
    sourceClass?: 'client' | 'bennie';
    // 2026-09-14 (client feedback / Regan Sun): the full display path — the catalog's own
    // local_path verbatim (already the full relative path, including the file name; corrected
    // 2026-09-15 — local_path was never folder-only, so nothing gets appended onto it) — for
    // this citation's source — see SectionModels.cs CitationSource.LocalPath. Absent when the
    // source has no local_path (a plain single-file upload) or on citations built before this
    // field existed. Persist-then-render: only render this, never recompute it.
    localPath?: string;
  };
  excerpt: string;
}

export type Artifact =
  | { kind: 'table_spec'; id: string; title?: string; spec: Record<string, unknown> }
  | { kind: 'chart_spec'; id: string; title?: string; spec: Record<string, unknown> }
  | { kind: 'metric'; id: string; title?: string; value: number | string; unit?: string };

/**
 * TD-438 — a curated-knowledge figure's fetchable URL, keyed by its `ckfigure://` reference.
 *
 * The FE cannot BUILD one of these. A CK figure URL carries a short-lived, single-figure
 * capability (`?cap=`) that only the CK plane can mint — it is HMAC-signed, bound to that one
 * blob, and carries the deployment identity the licence gate re-checks at redemption. bennie-api
 * relays the finished URL and nothing else; the per-deployment subscription key that bought it
 * never leaves the server, and the bytes never pass through bennie-api at all (ADR-029 L2).
 *
 * The URL is ABSOLUTE, composed server-side from the CK plane's own `CK_FIGURE_PUBLIC_BASE_URL`.
 * That is deliberate: the FE holds no gateway hostname, so a deployment's browsers cannot be
 * pointed at another deployment's plane by a stale front-end build.
 */
export type CkFigureUrlMap = Readonly<Record<string, string>>;

/**
 * The WIRE shape of the above: a LIST of pairs, never a JSON object keyed by reference.
 * `HttpBennieClient.fetch` runs every response through `transformKeys`, a DEEP snake_case →
 * camelCase key rewrite — and a reference used as an object key would be silently mangled
 * (`…/p1_i0.png` → `…/p1I0.png`), so every lookup would miss and fail loud for a reason no one
 * would find. As pair VALUES the references pass through untouched. Both property names here are
 * underscore-free for the same reason.
 */
export interface CkFigureUrlPair {
  reference: string;
  url: string;
}

/** Build the lookup map the image resolver takes from the wire list. Absent list ⇒ undefined. */
export function toCkFigureUrlMap(pairs: CkFigureUrlPair[] | undefined): CkFigureUrlMap | undefined {
  if (!pairs || pairs.length === 0) return undefined;
  return Object.fromEntries(pairs.map((p) => [p.reference, p.url]));
}

export interface SectionVersion {
  /**
   * The persisted `section_versions` row id (`{sectionId}_v{N}`), read off the versions payload.
   * It is what an inline edit sends back as `base_version_id` to declare which version it branched
   * from, so the API can refuse a save whose base has moved (409). OPTIONAL because not every
   * SectionVersion in this client comes from a persisted read: the regen SSE `complete` event
   * carries content and a version NUMBER but no row id. Do not synthesize one from `sectionId` and
   * `version` — a caller that does not have the id must say so and fail, not guess it.
   */
  id?: string;
  version: number;
  createdAt: string;
  status: SectionStatus;
  content: string;
  citations?: Citation[];
  artifacts?: Artifact[];
  feedback?: string;
  /** Provenance label carried by the versions payload. */
  source?: string;
  /**
   * TD-438 — capability URLs for the `ckfigure://` references this section's content and
   * citations carry, minted by the CK plane in the same round-trip that fetched the content.
   * Absent when the section cites no curated figure. The same union map is attached to every
   * version of the section: a lookup is by exact reference, so sharing it is free, and the
   * version picker can render any of them without a second fetch.
   */
  ckFigureUrls?: CkFigureUrlMap;
}

export interface Section {
  id: string;
  workspaceId: string;
  planItemId: string;
  title: string;
  fullTitle?: string;
  level?: number;
  status: SectionStatus;
  // Mirrors the render-shape field on PlanItem. The `executive_summary` value is
  // additionally written into this column when the section row belongs to the ES
  // category (root or dotted-id descendant) — so FE consumers reading section_type
  // alone can identify ES rows for chip rendering / retry-button routing without
  // re-fetching the plan.
  sectionType?: 'report' | 'test_run' | 'table' | 'verbatim' | 'executive_summary' | 'reference';
  latestVersion?: number;
  hasVersion?: boolean;
  versionStatus?: string;
  createdAt?: string;
  hasSubItems?: boolean;
  // CR6 Phase 3B (F-007): diagnostic fields populated when status is generation_failed
  // or context_unavailable. All optional — success-path sections leave them undefined.
  // Backend serializes from snake_case; HttpBennieClient maps to these camelCase fields.
  errorCode?: string;
  errorMessage?: string;
  errorStage?: string;
  retryCount?: number;
  failedAt?: string;
  lastErrorDetail?: Record<string, unknown>;
  // The section version whose text was written by a model that was not shown the section's figures.
  // Meaningful only while it equals the section's current version.
  figuresNotShownVersion?: number;
  // CR6 Phase 3C-Gate (F-008): populated on the executive-summary root when
  // status is 'exec_summary_blocked'. Null otherwise.
  blockingSections?: BlockingSection[];
}

// ADR-067: the persisted, source-numbered reference LIST (a workspace-scoped docstore
// artifact, NOT a section) returned by GET /workspaces/{id}/plan/{planId}/reference-list.
// The list is built DETERMINISTICALLY at finalization from the body's normalized citations
// — never LLM-generated — so each entry is a verbatim metadata card for one source document.
// Snake_case wire fields (reference_number / document_role / data_type / content_description
// / entry_text) are camelCased by HttpBennieClient.transformKeys — post-transform shapes.
export interface ReferenceEntry {
  referenceNumber: number;
  filename: string;
  /** 2026-09-14 (client feedback / Regan Sun): the full display path — the file-catalog row's
   *  OWN `local_path` verbatim (already the full relative path, including the file name;
   *  corrected 2026-09-15 — local_path was never folder-only, so nothing gets appended onto it)
   *  when the source carries one (a folder / `.zip` / `.egp` upload), else identical to
   *  `filename`. Absent on entries persisted before this field existed. Display-only — never a
   *  join key; `filename` keeps that role unchanged. */
  displayPath?: string | null;
  documentRole?: string | null;
  dataType?: string | null;
  contentDescription?: string | null;
  /** Composed, labeled+humanized metadata-card line
   *  `filename · Document Role: <H> · Data Type: <H> · description` (absent fields omitted) —
   *  persisted for the section content + the DOCX appendix (which splits it on ` · ` into
   *  soft-broken labeled lines). The reference drawer renders from the RAW structured fields
   *  above (filename / documentRole / dataType / contentDescription), not from this string. */
  entryText?: string | null;
}

export interface ReferenceList {
  entries: ReferenceEntry[];
  generatedAt?: string | null;
}

/** Response of the reference-list read endpoint (`{ reference_list }` only — the list is
 *  a docstore artifact, not a section, so there is no section id). The HTTP layer maps the
 *  endpoint's 404 ("reference list not built yet") to a null result, so a non-null value
 *  always carries a real, persisted list. */
export interface ReferenceListResponse {
  referenceList: ReferenceList;
}

export interface ExportRequest {
  workspaceId: string;
  sectionIds?: string[];
  sectionVersions?: Array<{ sectionId: string; sectionVersionId: string }> | Record<string, number>;
  format?: 'docx';
  includeCitationMetadata?: boolean;
  /** The persisted report version to export (development / validation / auditing). Absent = the active version. */
  reportVersionId?: string;
}

export interface ExportResult {
  downloadUrl: string;
  /**
   * ➕ **B14 `W-12`** — the id of the `exports` row this export wrote, and the handle
   * `POST /api/models/{modelId}/mdr/from-export` names it by.
   *
   * ⛔ **NOT DERIVED FROM `downloadUrl`.** The id IS in that path — `BuildExportBlobKey`
   * puts it in its own segment — but parsing it out would bind this client to a blob-key
   * GRAMMAR the backend treats as an implementation detail, and which already changed once
   * (2026-08-21, when the segment was introduced). It is published on the response instead.
   *
   * Optional because a pre-W-12 API does not send it (the promotion window). A caller that
   * cannot see it must render NO promote control — never a disabled one, which would tell
   * the user a capability exists and is temporarily unavailable.
   */
  exportId?: string;
  /**
   * ➕ **B14 `W-12`** — the exporting workspace's parent model, as the export recorded it.
   *
   * **Null/absent is a REAL and PERMANENT state, not a loading step** — ruling `R9`: a
   * pre-`0054` workspace has no parent model, is never backfilled, and that set never
   * drains. ⛔ The correct rendering is ABSENCE of the promote control, not a spinner and
   * not a retry.
   */
  modelId?: string | null;
  dataGapSections?: string[];  // EXPORT-1 (CR-3): sections containing [DATA GAP:] markers
  // Cleanup-time persistent warning. Wave 1 (2026-04-29) made this non-mutating at export
  // time — it reflects the cleanup outcome only, unchanged across /export calls. The
  // post-fallback re-read (D2-N1) was removed in the same wave. 🔴 2026-09-25 review round 3
  // — `templateStylePreserved` FULL-DELETED: it was captured here but never rendered by
  // anything since Wave 2.C (`WorkspacePage.tsx`'s onExport comment records that dead-code
  // removal); the persistent banner's actual source is the plan-fetch response's
  // `templateStyleStatus`, not this export response.
  templateStyleWarning?: string | null;
  // Wave 1 — per-export indicator and one-shot warning. `templateStyleUsedThisExport`
  // is "A" or "B" telling which render path actually produced THIS DOCX (Path A
  // template-anchored vs. Path B from-scratch). `templateStyleWarningThisExport`
  // is non-null when Path A was attempted but failed and we fell back to Path B —
  // the FE should surface this via Snackbar so the user knows generic styling was
  // applied for this file and that the workspace's template preservation is still
  // intact for future exports (no state mutation occurred).
  templateStyleUsedThisExport?: 'A' | 'B' | null;
  templateStyleWarningThisExport?: string | null;
  sizeBytes?: number;  // Bytes reported by render_docx; surfaces empty-file failures loudly.
}

// Test-run inflow types removed (batch-12 Stage 2). Reggie (Toolbox) is canonical; its
// types live in apps/web/src/views/toolbox/reggieApi.ts. The `'test_run'` value retained
// in PlanItem.type / Section.sectionType remains as legacy plan-gen output until the
// prompt-chain sweep (C3-117e) removes it.

// RCF-1 Phase 1D: Evidence map types for file-to-section mapping review
export interface EvidenceMapping {
  fileId: string;
  coversSections: string[];
}

export interface EvidenceMapFile {
  id: string;
  fileName: string;
  contentDescription?: string;
  dataType?: string;
  coversSections: string[];
  enrichmentStatus?: string;
  localPath?: string;
}

export interface EvidenceMapSection {
  id: string;
  title: string;
  type?: string;
}

export interface EvidenceMapResponse {
  workspaceId: string;
  files: EvidenceMapFile[];
  sections: EvidenceMapSection[];
}

// ---------------------------------------------------------------------------
// B13 WI-71 — THE PROGRESS CONTRACT (walk items 12, 20)
//
// One vocabulary, declared once, for the long-running operation the product
// draws a progress affordance for. Before this block the same union was spelled
// out twice by hand (BennieClient.ts's inline `onProgress` parameter type and
// HttpBennieClient.ts's private `PlanProgressPhase`) and the two had already
// drifted from what the backend emits, in BOTH directions:
//
//   - MISSING (BE emits, FE has no member and therefore no render branch):
//     `preparing`, `enrichment_rate_limited`, `enrichment_batch_failed`
//   - PHANTOM (FE declares, no producer anywhere in the estate):
//     `parsing`, `section_done` — the latter stopped being emitted at CR13 F-002
//
// Two hand-maintained copies of one BE-owned vocabulary is why the drift was
// invisible. Both spellings now DERIVE from here, so a BE phase added without an
// FE branch is a compile error at the ladder's `assertNever`, not a blank card.
//
// 🔴 THE LOAD-BEARING RULE OF THIS CONTRACT — `progress` IS OPTIONAL, AND ITS
// ABSENCE IS A FIRST-CLASS VALUE MEANING "UNKNOWN".
// A consumer that receives no `progress` MUST render an indeterminate affordance.
// It must NOT substitute a number of its own: not a step-count ratio, not a
// phase-keyed constant, not an elapsed-time ramp. Those are hardcoded fallbacks
// under `core-principles.md` "No Fallbacks, No Mask-Overs", and a bar that
// invents its own position is precisely the defect walk items 12 and 20 reported.
// The in-tree precedent is `usePlanProgress.ts` ("report the denominator the
// server actually sent" — a missing total surfaces as `null`) and
// `useETLProgress.ts` ("Signal-driven only (no time-based ramp)").
// ---------------------------------------------------------------------------

/**
 * Plan-generation phases, derived from the BE emitter set in
 * `PlanEndpoints.cs` (every `phase = "…"` on an `EmitEventAsync("status", …)`).
 * `heartbeat` is deliberately absent: the transport filters it before dispatch,
 * so it is a keepalive and never a phase a consumer renders.
 */
export const PLAN_PROGRESS_PHASES = [
  'preparing',
  'analyzing',
  'sections_found',
  'enriching',
  // B13 WI-72 REMOVED `enrichment_rate_limited` FROM THIS LIST, ON PURPOSE.
  // WI-71 added it because the backend genuinely emitted it. WI-72 then changed
  // WHAT the backend emits: exhausted enrichment retries now route through
  // `FailPlanGenAsync` (`phase = "plan_failed"`, `error_class =
  // "enrichment_rate_limited"`), which also writes `workflow_state = plan_failed`
  // and releases the `plan_gen` lock — neither of which the old status-shaped
  // frame did, so the workspace used to be left permanently mid-generation with
  // a leaked lock that 409'd the user's retry.
  // Keeping the phase here would recreate the exact phantom member (`parsing`,
  // `section_done`) that WI-71 deleted: a phase the FE believes in with no
  // producer. The `assertPhaseHandled` guard is what made this removal a
  // mechanical, compiler-checked edit rather than a hopeful one.
  'enrichment_batch_failed',
  'verifying_plan',
  'preparing_template',
  'finalizing_template',
  'finalizing',
  'complete',
  'error',
  'plan_failed',
] as const;

export type PlanProgressPhase = (typeof PLAN_PROGRESS_PHASES)[number];

// ⚠️ THERE IS DELIBERATELY NO `SECTION_REGEN_PHASES` UNION HERE, AND THE REASON IS
// WORTH THE LINES BECAUSE THE OBVIOUS INSTINCT IS TO ADD ONE.
//
// A first draft of this file declared one (`regen_mode`, `phase2c_dispatch`,
// `retrieving_context`, `context_ready`, `generating`, `finalizing`) for symmetry
// with the plan vocabulary above. It was deleted before merge, for two reasons that
// both still hold:
//
//  1. IT HAD NO CONSUMER. The regen dialog's fix was to STOP switching on the phase
//     name — every frame now renders the backend's own localized message through one
//     path, which is what makes it exhaustive over the backend's phase set by
//     construction instead of by a lookup table someone must remember to extend. A
//     declared vocabulary nothing reads is a speculative abstraction
//     (`core-principles.md` "Simplicity first"), and it would have been read later as
//     an invitation to reintroduce the phase-keyed switch that WAS the defect.
//
//  2. IT TRIPPED `SectionStatusDriftScanTests` — CORRECTLY. `context_ready` is also a
//     canonical `SectionStatus`, so declaring it here moved this definition file's
//     pinned literal count 8 → 9 and turned the build red. That scan exists because
//     "what holds a numbered phase gate" escaped five consecutive review rounds, and
//     its registry says in as many words: do not bump the number reflexively — the
//     number moving is the scan asking whether what you just wrote belongs here at
//     all. Asked and answered: it did not. The pin is untouched.
//
// The collision itself is real and is the thing to remember: the regen pipeline emits
// an SSE phase named `context_ready` AND the section lands in a persisted status
// named `context_ready`. Same word, two planes, different lifetimes — one is a
// transient wire label, the other is durable state a gate reads. If a future change
// genuinely needs the regen phase vocabulary on the client (WI-73's persisted-phase
// rehydration is the plausible caller), declare it WITH a consumer in the same change,
// and expect this scan to ask about `context_ready` again.

/**
 * Runtime narrowing for a plan phase that arrived off the wire — specifically
 * from the PERSISTED milestone record (`GET /plan/generation-progress`), which is
 * plain JSON and therefore unchecked. The rehydrate path used to write
 * `phase: milestones.phase` verbatim into card state; a value the card has no
 * branch for renders as an EMPTY card, which is what a mid-generation refresh
 * during the `preparing` window actually produced.
 */
export function isPlanProgressPhase(value: string | undefined | null): value is PlanProgressPhase {
  return typeof value === 'string' && (PLAN_PROGRESS_PHASES as readonly string[]).includes(value);
}

/**
 * Compile-time exhaustiveness marker for a phase ladder. Reaching this call with
 * anything other than `never` is a TYPE error, which is the whole point: it makes
 * "the backend added a phase and the UI silently drew nothing" impossible to ship.
 *
 * DELIBERATELY DOES NOT THROW, unlike `reportSections.assertNever`. That one guards
 * pure projections where a throw is recoverable at the call site. These ladders run
 * inside live SSE callbacks, and throwing there would tear down a generation the
 * backend is still successfully running — turning an unrecognised LABEL into a lost
 * plan. The caller logs the unknown phase; this call exists for the compiler.
 */
export function assertPhaseHandled(_phase: never): void {
  /* intentionally empty — the `never` parameter type IS the assertion */
}

// SSE Recovery: Active operations tracked for frontend hydration/reconnect
export interface ActiveOperation {
  startedAt: string;
  planId?: string;
  sectionId?: string;
  total?: number;
  // D2 (2026-07-30): the liveness timestamp a LIVE owner re-stamps on a ~30s cadence
  // (workflow_state.active_operations.{op}.last_heartbeat). Always on the wire — GET
  // /api/status/{ws}/{wf} returns the whole workflow_state doc — but previously undeclared, so
  // the FE could not tell a running operation from a dead one and the holistic-validation
  // indicator spun forever. Absent on operations acquired before the heartbeat writer landed.
  lastHeartbeat?: string;
  /** `chat_turn` lease only: the ordinal of the turn holding it (decimal string); what a Stop names after a refresh. */
  turnOrdinal?: string;
  // W5-A holistic phase marker (active_operations.holistic_validation.phase): llm_validating →
  // applying_results. Progress evidence for phases that persist no heartbeat.
  phase?: string;
  /**
   * W2-16 (2026-09-08) — the i18n key for `phase`'s copy, written beside it by
   * `WorkflowStateRepository.UpdateOperationPhaseAsync` (`phase_message_code`) and camelCased on
   * the way in by `HttpBennieClient.parseJson`.
   *
   * DECLARED HERE BECAUSE IT WAS ALREADY ON THE WIRE AND READ BY NOTHING — the same shape as the
   * `chatTurn` note below. `GET /api/status/{ws}/{wf}` returns the whole `workflow_state` doc, so
   * every section-regen lease has carried this field since B13 WI-73; the only consumer was the
   * feedback dialog's own `GET /sections/regen-progress` poll, which re-fetched a value the
   * workflow-state poll was already holding (and holding FRESHER — that poll runs at 3s during a
   * regen against the dialog's 5s).
   *
   * It is a code, never prose: resolved through `resolveMessageCode(…, 'progress')`, the SAME
   * catalogue and resolver the live SSE stream's `message_code` goes through. That is what makes a
   * polled label and a streamed label incapable of disagreeing, and it is why there is no
   * phase→copy map on the client.
   */
  phaseMessageCode?: string;
  // Kill-failed-state (2026-04-19): halt flag written by the section-gen infra classifier
  // (ContentGenStreamPipeline.cs). Cleared by POST /retry-generation. Replaces the old
  // workflow_state=failed stamp as the workspace-level recoverable-halt signal.
  infrastructureError?: boolean;
  errorCategory?: string;
  errorMessage?: string;
  haltedAt?: string;
}

export interface ActiveOperations {
  planGen?: ActiveOperation;
  sectionGen?: ActiveOperation;
  sectionRegen?: ActiveOperation;
  /**
   * W-17 (2026-09-05): the `chat_turn` lease — held and heartbeated by `ChatTurnLease` for the whole
   * duration of a conversational turn.
   *
   * NAMED here rather than left to the index signature below, and the comment on that signature says
   * why: relying on it is how the FE ended up blind to a stranded lease, and `chat_turn` was the
   * FOURTH key to repeat that. It was on this payload the whole time and read by nothing, which is
   * the entire reason turn liveness could not survive a remount.
   */
  chatTurn?: ActiveOperation;
  // D2: the BE writes active_operations under an OPEN set of operation keys (section_regen_batch,
  // completeness, holistic_validation, phase2c_strand_refire, …) and adds new ones as pipelines
  // are added. The three named members above are the ones the FE reads by name; the index
  // signature declares the rest, which the liveness scan in utils/holisticValidationStall.ts
  // iterates. Naming only three of them was how the FE ended up blind to a stranded lease.
  [operationKey: string]: ActiveOperation | undefined;
}

// SF-055: Workspace message log — unified, ordered event stream per workspace
export type WorkspaceMessageType =
  | 'plan_generated'
  | 'plan_revised' // Deprecated: no longer emitted by API; kept for backward compat with existing persisted records
  | 'plan_approved'
  | 'data_ingestion_complete'
  | 'completeness_report'
  | 'chat_user'
  | 'chat_assistant';

export interface WorkspaceMessage {
  id: string;
  workspaceId: string;
  sequence: number;
  role: 'system' | 'user' | 'assistant';
  type: WorkspaceMessageType;
  content: string;
  metadata: Record<string, unknown>;
  createdAt: string;
  // WI-07 (ADR-076 W2): embedded deep-retrieve citations on chat_assistant messages —
  // the POLLED persisted source the chat citation pop-up renders from (persist-then-render;
  // never from the /chat/query response payload). Absent on every non-deep message.
  citations?: Citation[];
}
